import api from "api/api";
import { DELIVERY_NOTE_REQUEST_TIMEOUT } from "constants/constants";
import { createRawFileQuery, getFileByIdQuery } from "api/files/queries";
import { buildQueryParams } from "utils/queriesHelper";

export const getDeliveryNoteTemplatesQuery = async ({ userScope }) =>
  await api.get(`/datatrack/delivery-note/templates?scope=${userScope}`);

export const createDeliveryNoteTemplateQuery = async ({ data, userScope }) =>
  await api.post(
    `/datatrack/delivery-note/template?scope=${userScope}`,
    data,
    { timeout: DELIVERY_NOTE_REQUEST_TIMEOUT }
  );

export const updateDeliveryNoteTemplateQuery = async ({
  id,
  data,
  userScope,
}) =>
  await api.put(
    `/datatrack/delivery-note/template/${id}?scope=${userScope}`,
    { ...data, id },
    { timeout: DELIVERY_NOTE_REQUEST_TIMEOUT }
  );

export const deleteDeliveryNoteTemplateQuery = async ({ id, userScope }) =>
  await api.delete(
    `/datatrack/delivery-note/template/${id}?scope=${userScope}`,
    undefined,
    { timeout: DELIVERY_NOTE_REQUEST_TIMEOUT }
  );

export const setDeliveryNoteTemplateFavoriteQuery = async ({
  id,
  isFavorite,
  userScope,
}) =>
  await api.patch(
    `/datatrack/delivery-note/template/${id}/favorite?scope=${userScope}`,
    { is_favorite: isFavorite }
  );

export const setDeliveryNoteTemplatePublicQuery = async ({
  id,
  isPublic,
  userScope,
}) =>
  await api.patch(
    `/datatrack/delivery-note/template/${id}/public?scope=${userScope}`,
    { is_public: isPublic }
  );

export const uploadDeliveryNoteImageQuery = async ({ blob }) => {
  const created = await createRawFileQuery(await blob.arrayBuffer());
  return created?.uri || null;
};

const imageByFileId = new Map();

const decodeImage = (url) =>
  new Promise((resolve) => {
    if (!url) {
      resolve(null);
      return;
    }
    const image = new Image();
    image.onload = () => resolve(url);
    image.onerror = () => resolve(null);
    image.src = url;
  });

export const getDeliveryNoteImageQuery = ({ fileId }) => {
  if (!imageByFileId.has(fileId)) {
    imageByFileId.set(
      fileId,
      getFileByIdQuery({ id: fileId, isBase64Request: true })
        .then((base64) => (base64 ? `data:image/png;base64,${base64}` : null))
        .then(decodeImage)
        .catch((e) => {
          imageByFileId.delete(fileId);
          throw e;
        })
    );
  }
  return imageByFileId.get(fileId);
};

export const getReceivedDeliveryNoteTemplatesQuery = async ({ userScope }) =>
  await api.get(
    `/datatrack/delivery-note/templates/received?scope=${userScope}`
  );

export const importReceivedDeliveryNoteTemplateQuery = async ({
  shareId,
  userScope,
}) =>
  await api.post(
    `/datatrack/delivery-note/template/received/${shareId}/import?scope=${userScope}`,
    undefined,
    { timeout: DELIVERY_NOTE_REQUEST_TIMEOUT }
  );

export const dismissReceivedDeliveryNoteTemplateQuery = async ({
  shareId,
  userScope,
}) =>
  await api.delete(
    `/datatrack/delivery-note/template/received/${shareId}?scope=${userScope}`,
    undefined,
    { timeout: DELIVERY_NOTE_REQUEST_TIMEOUT }
  );

export const getPublicDeliveryNoteTemplatesQuery = async ({ userScope }) =>
  await api.get(`/datatrack/delivery-note/templates/public?scope=${userScope}`);

export const importPublicDeliveryNoteTemplateQuery = async ({
  templateId,
  userScope,
}) =>
  await api.post(
    `/datatrack/delivery-note/template/public/${templateId}/import?scope=${userScope}`,
    undefined,
    { timeout: DELIVERY_NOTE_REQUEST_TIMEOUT }
  );

export const shareDeliveryNoteTemplateQuery = async ({
  id,
  colleagueIds,
  message,
}) =>
  await api.post(
    `/datatrack/delivery-note/template/${id}/share`,
    { colleague_ids: colleagueIds, message },
    { timeout: DELIVERY_NOTE_REQUEST_TIMEOUT }
  );

export const getDeliveryNoteNumberQuery = async ({ templateId, userScope }) =>
  await api.get(
    `/datatrack/delivery-note/template/${templateId}/number?scope=${userScope}`
  );

export const createDeliveryNoteQuery = async ({ data, userScope }) =>
  await api.post(`/datatrack/delivery-note/note?scope=${userScope}`, data);

export const updateDeliveryNoteQuery = async ({ id, data, userScope }) =>
  await api.put(`/datatrack/delivery-note/note/${id}?scope=${userScope}`, data);

export const deleteDeliveryNoteQuery = async ({ id, userScope }) =>
  await api.delete(`/datatrack/delivery-note/note/${id}?scope=${userScope}`);

export const getDeliveryNotesQuery = async ({ userScope, orderBy }) =>
  await api.get(
    `/datatrack/delivery-note/notes?${buildQueryParams({
      scope: userScope,
      "order-by": orderBy,
    })}`
  );
